import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/base/Layout';

// تعريف نوع البيانات للصورة
interface GalleryImage {
  id: number;
  src: string;
  title: string;
}

export default function GalleryPage() {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);

  // 🟢 هذا هو السحر: دالة تفحص الصورة فعلياً
  const checkImageExists = (src: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true); // الصورة حملت بنجاح
      img.onerror = () => resolve(false); // الصورة غير موجودة أو معطوبة
      img.src = src;
    });
  };

  useEffect(() => {
    const loadGallery = async () => {
      setLoading(true);
      const foundImages: GalleryImage[] = [];
      const maxImages = 100; // سنفحص حتى 100

      // مصفوفة وعود (Promises) لفحص كل الصور في نفس الوقت (أسرع بكثير)
      const checks = [];

      for (let i = 1; i <= maxImages; i++) {
        // سنفحص عدة صيغ لكل رقم
        const variations = [
          `/images/${i}.jpg`,
          `/images/${i}.JPG`,
          `/images/${i}.jpeg`,
          `/images/${i}.png`
        ];

        // ننشئ وعد لفحص الصورة رقم i
        const checkPromise = async () => {
          for (const src of variations) {
            const exists = await checkImageExists(src);
            if (exists) {
              return { id: i, src, title: `Product Image ${i}` };
            }
          }
          return null;
        };

        checks.push(checkPromise());
      }

      // ننتظر انتهاء فحص جميع الصور
      const results = await Promise.all(checks);

      // نأخذ النتائج الصحيحة فقط (التي ليست null)
      const validResults = results.filter((img): img is GalleryImage => img !== null);

      setGalleryImages(validResults);
      setLoading(false);
    };

    loadGallery();
  }, []);

  const handlePrevious = () => {
    if (selectedIndex !== null) {
      const prevIndex = selectedIndex === 0 ? galleryImages.length - 1 : selectedIndex - 1;
      setSelectedIndex(prevIndex);
    }
  };

  const handleNext = () => {
    if (selectedIndex !== null) {
      const nextIndex = selectedIndex === galleryImages.length - 1 ? 0 : selectedIndex + 1;
      setSelectedIndex(nextIndex);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') handlePrevious();
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'Escape') setSelectedIndex(null);
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[40vh] md:h-[50vh] min-h-[300px] md:min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Beautiful%20Egyptian%20orange%20orchard%20at%20golden%20hour%20with%20rows%20of%20citrus%20trees%20laden%20with%20ripe%20fruits%20stunning%20agricultural%20landscape%20professional%20photography%20warm%20sunlight&width=1920&height=800&seq=101&orientation=landscape"
            alt="Egyptian Agricultural Landscape"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/70 via-emerald-900/50 to-emerald-900/80"></div>
        </div>
        <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
          <span className="inline-block px-4 py-1.5 bg-emerald-500/20 backdrop-blur-sm text-emerald-300 text-xs md:text-sm font-medium rounded-full mb-4 md:mb-6">
            Visual Journey
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            Our <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_auto] animate-gradient-text bg-clip-text text-transparent">Gallery</span>
          </h1>
          <p className="text-sm md:text-lg text-white/80 max-w-2xl mx-auto px-4">
            Pictures of nature while preparing orders - Explore our farms, facilities, and the journey of premium Egyptian produce from field to export.
          </p>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedIndex !== null && galleryImages[selectedIndex] && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedIndex(null)}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <button
            onClick={() => setSelectedIndex(null)}
            className="absolute top-4 right-4 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition-colors z-10 cursor-pointer"
          >
            <i className="ri-close-line text-xl md:text-2xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); handlePrevious(); }}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition-colors z-10 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-2xl md:text-3xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); handleNext(); }}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition-colors z-10 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-2xl md:text-3xl"></i>
          </button>

          <div className="max-w-5xl w-full px-12 md:px-20" onClick={(e) => e.stopPropagation()}>
            <img
              src={galleryImages[selectedIndex].src}
              alt={galleryImages[selectedIndex].title}
              className="w-full h-auto max-h-[70vh] object-contain rounded-lg md:rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* Gallery Grid Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <span className="inline-block px-4 py-1.5 bg-emerald-100 text-emerald-600 text-xs md:text-sm font-medium rounded-full mb-3 md:mb-4">
              Our Work
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              Photo <span className="bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 bg-[length:200%_auto] animate-gradient-text bg-clip-text text-transparent">Gallery</span>
            </h2>
            <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
              Explore our collection of images showcasing our premium Egyptian agricultural products and operations.
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center py-12">
               <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {/* Grid */}
          {!loading && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {galleryImages.map((image, index) => (
                <div
                  key={image.id}
                  onClick={() => setSelectedIndex(index)}
                  className="group relative overflow-hidden rounded-xl cursor-pointer aspect-square bg-gray-100"
                >
                  <img
                    src={image.src}
                    alt={image.title}
                    loading="lazy"
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-sm md:text-base font-semibold text-white truncate">
                      {image.title}
                    </h3>
                  </div>
                  <div className="absolute top-3 right-3 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-zoom-in-line text-white text-sm md:text-base"></i>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* رسالة في حال عدم وجود صور */}
          {!loading && galleryImages.length === 0 && (
              <div className="text-center py-10 text-gray-500">
                  No images found. Please check your 'public/images' folder.
              </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 bg-[length:200%_100%] animate-gradient-move"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 md:w-96 h-64 md:h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-64 md:w-96 h-64 md:h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6">
            Want to Visit Our Facilities?
          </h2>
          <p className="text-sm md:text-lg text-white/80 mb-6 md:mb-8">
            Schedule a tour of our farms and processing facilities to see our
            quality standards firsthand.
          </p>
          <Link
            to="/contact"
            className="inline-block px-6 md:px-8 py-3 md:py-4 bg-white text-emerald-600 text-sm md:text-base font-semibold rounded-full hover:shadow-xl transition-all duration-300 whitespace-nowrap"
          >
            Schedule a Visit
          </Link>
        </div>
      </section>
    </Layout>
  );
}